import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';


// Definir tipos para cualquier prop que el componente pueda aceptar
interface Suit {
  source: string;
  target: string;
  type: string;
}

interface ForceGraphProps {
  suits: Suit[];
}

interface LinkArc {
    // Asegúrate de que estas propiedades coincidan con la estructura de tus datos de enlace
    source: string | number | Node;
    target: string | number | Node;
  }

// Definir tipos para los nodos y enlaces
interface Node extends d3.SimulationNodeDatum {
    id: string;
    // Otras propiedades específicas de tu aplicación (si las hay)
}

interface Link extends d3.SimulationLinkDatum<Node> {
  type: string;
}

const ForceGraph: React.FC<ForceGraphProps> = ({ suits }) => {
    const ref = useRef<SVGSVGElement>(null);
    const typesRef = useRef<any>([]);
    const nodesRef = useRef<any>([]);
    const linksRef = useRef<any>([]);

  useEffect(() => {
    if (ref.current) {
      const width = 928;
      const height = 600;
      const types = Array.from(new Set(suits.map(d => d.type)));
      const nodes: Node[] = Array.from(new Set(suits.flatMap(l => [l.source, l.target])), id => ({id} as Node));
      const links: Link[] = suits.map(d => ({ ...d }));
      typesRef.current = types;
      nodesRef.current = nodes;
      linksRef.current = links;
      

      const color = d3.scaleOrdinal(types, d3.schemeCategory10);

      const simulation = d3.forceSimulation<Node>(nodesRef.current)
        .force("link", d3.forceLink(linksRef.current).id((d: d3.SimulationNodeDatum) => (d as Node).id))        
        .force("charge", d3.forceManyBody().strength(-400))
        .force("x", d3.forceX())
        .force("y", d3.forceY());

      const svg = d3.select(ref.current)
        .attr("viewBox", [-width / 2, -height / 2, width, height])
        .attr("width", width)
        .attr("height", height)
        .attr("style", "max-width: 100%; height: auto; font: 12px sans-serif;");

        svg.append("defs").selectAll("marker")
        .data(types)
        .join("marker")
          .attr("id", d => `arrow-${d}`)
          .attr("viewBox", "0 -5 10 10")
          .attr("refX", 15)
          .attr("refY", -0.5)
          .attr("markerWidth", 6)
          .attr("markerHeight", 6)
          .attr("orient", "auto")
        .append("path")
          .attr("fill", color)
          .attr("d", "M0,-5L10,0L0,5");
        
    // Configuración de zoom
    const zoom = d3.zoom<SVGSVGElement, unknown>()
    .scaleExtent([0.5, 10])
    .on("zoom", (event) => {
      svg.attr("transform", event.transform);
    });
    
    svg.call(zoom);
    
      const link = svg.append("g")
          .attr("fill", "none")
          .attr("stroke-width", 1.5)
          .selectAll("path")
          .data(links)
          .join("path")
          .attr("stroke", d => color(d.type))
          .attr("marker-end", d => `url(#arrow-${d.type})`);
        
        link.append("text")
            .attr("x", 8)
            .attr("y", "0.31em")
            .text("texto")
        .clone(true).lower()
            .attr("fill", "none")
            .attr("stroke", "white")
            .attr("stroke-width", 3);


          
    
      const node = svg.append("g")
          .attr("fill", "currentColor")
          .attr("stroke-linecap", "round")
          .attr("stroke-linejoin", "round")
          .selectAll<SVGGElement, Node>("g") // Asegúrate de que la selección sea de SVGGElement          
          .data(nodes)
          .join("g")
          .call(drag(simulation));
    
      node.append("circle")
          .attr("stroke", "white")
          .attr("stroke-width", 1.5)
          .attr("r", 4);
    
      node.append("text")
          .attr("x", 8)
          .attr("y", "0.31em")
          .text(d => d.id)
        .clone(true).lower()
          .attr("fill", "none")
          .attr("stroke", "white")
          .attr("stroke-width", 3);

    simulation.on("tick", () => {
        link.attr("d", linkArc);
        node.attr("transform", d => `translate(${d.x},${d.y})`);
    });
          
      // Limpieza de efectos secundarios
      return () => {
        simulation.stop();
      };
    }
  }, [suits]);

function linkArc(d: LinkArc) {
    const source = d.source as Node;
    const target = d.target as Node;
    const r = Math.hypot((target.x || 0) - (source.x || 0), (target.y || 0) - (source.y || 0));
    return `
        M${source.x || 0},${source.y || 0}
        A${r},${r} 0 0,1 ${target.x || 0},${target.y || 0}
    `;
}

  const drag = (simulation: d3.Simulation<Node, undefined>) => {
    const dragstarted = (event: d3.D3DragEvent<SVGGElement, Node, Node>, d: Node) => {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    };
  
    const dragged = (event: d3.D3DragEvent<SVGGElement, Node, Node>, d: Node) => {
      d.fx = event.x;
      d.fy = event.y;
    };
  
    const dragended = (event: d3.D3DragEvent<SVGGElement, Node, Node>, d: Node) => {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    };
  
    return d3.drag<SVGGElement, Node>()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  };

  return <svg ref={ref}></svg>;
};

export default ForceGraph;